import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Check, RefreshCw, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import KenbrightLogo from '../assets/images/KenbrightLogo';
import { ssoEnabled, login as ssoLogin } from '../auth/keycloak';

const MicrosoftLogo = () => (
    <svg className="w-5 h-5" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
);

const LoginPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [ssoRedirecting, setSsoRedirecting] = useState(false);

    const { setSession, logoutMessage, clearLogoutMessage } = useAuth();

    // Clear the logout message after 10 seconds
    useEffect(() => {
        if (logoutMessage) {
            const timer = setTimeout(() => clearLogoutMessage(), 10000);
            return () => clearTimeout(timer);
        }
    }, [logoutMessage, clearLogoutMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await authService.login({ email, password });
            const loginData = result.data || result;
            console.log('[LOGIN] Response:', JSON.stringify(loginData, null, 2));

            // 1. MFA required — don't store tokens, navigate to MFA page
            if (loginData.mfaRequired) {
                navigate('/mfa-verify', {
                    state: { username: email, mfaMethod: loginData.mfaMethod }
                });
                return;
            }

            // 2. Password change required — store tokens (needed for change-password API), navigate
            if (loginData.passwordChangeRequired) {
                localStorage.setItem('nexus_token', loginData.token);
                if (loginData.refreshToken) {
                    localStorage.setItem('nexus_refresh_token', loginData.refreshToken);
                }
                navigate('/change-password', {
                    state: { email: loginData.email, tempPassword: password }
                });
                return;
            }

            // 3. Normal login — store session, navigate to dashboard
            setSession(loginData);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await authService.forgotPassword(resetEmail);
            setResetSuccess(true);
        } catch (err) {
            // Still show success to prevent email enumeration
            setResetSuccess(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-start justify-center pt-32 p-4">
            <div className="w-full max-w-md relative z-10">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-60 h-48">
                            <KenbrightLogo className="w-full h-full" />
                        </div>
                    </div>

                    {!showForgotPassword ? (
                        /* Login Form */
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {logoutMessage && (
                                <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg flex items-center gap-2">
                                    <Info className="w-5 h-5 flex-shrink-0" />
                                    <span className="text-sm">{logoutMessage}</span>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}

                            {ssoEnabled() && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => { setSsoRedirecting(true); ssoLogin(); }}
                                        disabled={ssoRedirecting}
                                        className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-50 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                        {ssoRedirecting ? <RefreshCw className="w-5 h-5 animate-spin" /> : <MicrosoftLogo />}
                                        Sign in with Microsoft
                                    </button>
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <div className="flex-1 h-px bg-gray-200" />
                                        or with a password
                                        <div className="flex-1 h-px bg-gray-200" />
                                    </div>
                                </>
                            )}

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-10 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-10 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                                            placeholder="Enter your password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        id="remember"
                                        type="checkbox"
                                        className="w-4 h-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
                                        Remember me
                                    </label>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowForgotPassword(true)}
                                    className="text-sm text-blue-600 hover:text-blue-700 transition font-medium"
                                >
                                    Forgot Password?
                                </button>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                        Signing In...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </form>
                    ) : (
                        /* Forgot Password Form */
                        <form onSubmit={handleForgotPassword} className="space-y-6">
                            <div className="text-center mb-4">
                                <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Enter your email address and we'll send you a reset link
                                </p>
                            </div>

                            {resetSuccess ? (
                                <div className="bg-green-50 border border-green-200 p-4 rounded-lg flex items-center gap-3">
                                    <Check className="w-5 h-5 text-green-600" />
                                    <div>
                                        <p className="text-sm font-medium text-green-800">Reset link sent!</p>
                                        <p className="text-xs text-green-700 mt-1">Check your email for instructions</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {error && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
                                            <AlertCircle className="w-5 h-5" />
                                            <span className="text-sm">{error}</span>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="email"
                                                value={resetEmail}
                                                onChange={(e) => setResetEmail(e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-10 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                                                placeholder="admin@nexus.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </>
                            )}

                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForgotPassword(false);
                                        setResetEmail('');
                                        setResetSuccess(false);
                                        setError('');
                                    }}
                                    className="text-blue-600 hover:text-blue-700 text-sm transition font-medium"
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
